// src/pages/trading-dashboard/components/MarketChart.jsx
import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';

const MarketChart = ({ selectedMarket, isLoading }) => {
  const [chartType, setChartType] = useState('line');
  const [timeframe, setTimeframe] = useState('24h');
  const [chartData, setChartData] = useState([]);

  const timeframeOptions = [
    { id: '1h', label: '1H' },
    { id: '24h', label: '24H' },
    { id: '7d', label: '7D' },
    { id: '30d', label: '30D' },
    { id: '90d', label: '90D' }
  ];

  const chartTypes = [
    { id: 'line', label: 'Line', icon: 'TrendingUp' },
    { id: 'area', label: 'Area', icon: 'BarChart3' },
    { id: 'candle', label: 'Candle', icon: 'Activity' }
  ];

  useEffect(() => {
    if (selectedMarket?.priceHistory) {
      // Process price history data based on timeframe
      let processedData = [...selectedMarket?.priceHistory];
      
      // Filter data based on timeframe
      const now = new Date();
      switch (timeframe) {
        case '1h':
          processedData = processedData?.slice(-12); // Last 12 data points
          break;
        case '24h':
          processedData = processedData?.slice(-24);
          break;
        case '7d':
          processedData = processedData?.slice(-7);
          break;
        case '30d':
          processedData = processedData?.slice(-30);
          break;
        case '90d':
          processedData = processedData?.slice(-90);
          break;
        default:
          break;
      }

      // Add some price variation for realism
      processedData = processedData?.map((point, index) => ({
        ...point,
        timestamp: new Date(point?.date)?.getTime(),
        yesPrice: Math.max(0.01, Math.min(0.99, point?.yesPrice + (Math.random() - 0.5) * 0.02)),
        noPrice: Math.max(0.01, Math.min(0.99, point?.noPrice + (Math.random() - 0.5) * 0.02)),
        volume: point?.volume + Math.floor((Math.random() - 0.5) * 500)
      }));

      setChartData(processedData);
    }
  }, [selectedMarket, timeframe]);

  const formatTooltipValue = (value, name) => {
    if (name === 'volume') {
      return [`$${value?.toLocaleString()}`, 'Volume'];
    }
    return [`$${value?.toFixed(3)}`, name === 'yesPrice' ? 'Yes Price' : 'No Price'];
  };

  const formatXAxisLabel = (timestamp) => {
    const date = new Date(timestamp);
    switch (timeframe) {
      case '1h':
        return date?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      case '24h':
        return date?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      case '7d': case'30d': case'90d':
        return date?.toLocaleDateString([], { month: 'short', day: 'numeric' });
      default:
        return date?.toLocaleDateString();
    }
  };

  if (!selectedMarket) {
    return (
      <div className="p-6 h-full flex items-center justify-center">
        <div className="text-center">
          <Icon name="BarChart3" size={48} className="text-text-secondary mx-auto mb-3" />
          <h3 className="font-body-semibold text-text-primary mb-2">Select a Market</h3>
          <p className="text-sm text-text-secondary">
            Choose a prediction market to view its price chart
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Chart Header */}
      <div className="flex items-center justify-between p-6 border-b border-border">
        <div>
          <h3 className="text-lg font-heading-semibold text-text-primary">
            Price Chart
          </h3>
          <p className="text-sm text-text-secondary">
            {selectedMarket?.title}
          </p>
        </div>

        <div className="flex items-center space-x-4">
          {/* Timeframe Selection */}
          <div className="flex items-center space-x-1 bg-surface-hover rounded-trading p-1">
            {timeframeOptions?.map((option) => (
              <button
                key={option?.id}
                onClick={() => setTimeframe(option?.id)}
                className={`px-3 py-1 text-xs font-body-medium rounded transition-all duration-trading-fast ${
                  timeframe === option?.id
                    ? 'bg-primary text-white shadow-sm' :'text-text-secondary hover:text-text-primary'
                }`}
              >
                {option?.label}
              </button>
            ))}
          </div>

          {/* Chart Type Selection */}
          <div className="flex items-center space-x-1 bg-surface-hover rounded-trading p-1">
            {chartTypes?.map((type) => (
              <button
                key={type?.id}
                onClick={() => setChartType(type?.id)}
                className={`flex items-center space-x-1 px-2 py-1 text-xs font-body-medium rounded transition-all duration-trading-fast ${
                  chartType === type?.id
                    ? 'bg-primary text-white shadow-sm' :'text-text-secondary hover:text-text-primary'
                }`}
              >
                <Icon name={type?.icon} size={12} />
                <span className="hidden sm:inline">{type?.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Chart Content */}
      <div className="flex-1 p-6">
        {isLoading ? (
          <div className="h-full flex items-center justify-center">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              <span className="text-text-secondary">Loading chart data...</span>
            </div>
          </div>
        ) : chartData?.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <Icon name="BarChart3" size={48} className="text-text-secondary mx-auto mb-3" />
              <p className="text-text-secondary">No chart data available</p>
            </div>
          </div>
        ) : (
          <div className="h-full">
            <ResponsiveContainer width="100%" height="100%">
              {chartType === 'area' ? (
                <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="yesGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="noGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#EF4444" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#EF4444" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" />
                  <XAxis 
                    dataKey="timestamp" 
                    stroke="#94A3B8"
                    fontSize={12}
                    tickFormatter={formatXAxisLabel}
                  />
                  <YAxis 
                    stroke="#94A3B8" 
                    fontSize={12}
                    domain={[0, 1]}
                    tickFormatter={(value) => `$${value?.toFixed(2)}`}
                  />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: '#1A2332',
                      border: '1px solid #2D3748',
                      borderRadius: '8px',
                      color: '#F8FAFC'
                    }}
                    formatter={formatTooltipValue}
                    labelFormatter={(timestamp) => formatXAxisLabel(timestamp)}
                  />
                  <Area
                    type="monotone"
                    dataKey="yesPrice"
                    stroke="#10B981"
                    strokeWidth={2}
                    fill="url(#yesGradient)"
                    name="yesPrice"
                  />
                  <Area
                    type="monotone"
                    dataKey="noPrice"
                    stroke="#EF4444"
                    strokeWidth={2}
                    fill="url(#noGradient)"
                    name="noPrice"
                  />
                </AreaChart>
              ) : (
                <LineChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" />
                  <XAxis 
                    dataKey="timestamp" 
                    stroke="#94A3B8"
                    fontSize={12}
                    tickFormatter={formatXAxisLabel}
                  />
                  <YAxis 
                    stroke="#94A3B8" 
                    fontSize={12}
                    domain={[0, 1]}
                    tickFormatter={(value) => `$${value?.toFixed(2)}`}
                  />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: '#1A2332',
                      border: '1px solid #2D3748',
                      borderRadius: '8px',
                      color: '#F8FAFC'
                    }}
                    formatter={formatTooltipValue}
                    labelFormatter={(timestamp) => formatXAxisLabel(timestamp)}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="yesPrice" 
                    stroke="#10B981" 
                    strokeWidth={3}
                    dot={{ fill: '#10B981', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, stroke: '#10B981', strokeWidth: 2 }}
                    name="yesPrice"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="noPrice" 
                    stroke="#EF4444" 
                    strokeWidth={3}
                    dot={{ fill: '#EF4444', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, stroke: '#EF4444', strokeWidth: 2 }}
                    name="noPrice"
                  />
                </LineChart>
              )}
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Chart Footer with Current Prices */}
      {selectedMarket?.outcomeTokens && (
        <div className="border-t border-border p-4">
          <div className="grid grid-cols-2 gap-4">
            {selectedMarket?.outcomeTokens?.map((token, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-surface-hover rounded-trading">
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${index === 0 ? 'bg-success' : 'bg-error'}`}></div>
                  <span className="font-body-medium text-sm text-text-primary">{token?.name}</span>
                </div>
                <div className="text-right">
                  <p className={`font-data text-lg ${index === 0 ? 'text-success' : 'text-error'}`}>
                    ${token?.price?.toFixed(3)}
                  </p>
                  <p className="text-xs text-text-secondary">
                    {token?.shares?.toLocaleString()} shares
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketChart;